import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type RecipeByCalories } from '../api/endpoints';
import { ApiException } from '../api/client';
import { RecipeCard } from '../components/RecipeCard';
import { Skeleton, ErrorState, EmptyState } from '../components/states';

const PRESETS = [300, 400, 500, 600] as const;
const MIN_KCAL = 50;
const MAX_KCAL = 2000;
const DEFAULT_KCAL = 500;

function clamp(n: number): number {
  return Math.min(MAX_KCAL, Math.max(MIN_KCAL, n));
}

// «Калории» — лента рецептов не более N ккал на порцию (зеркало CuisineScreen/FestiveScreen),
// от самых лёгких. Порог задаётся пресетами 300/400/500/600 или своим числом (с debounce и
// клампом 50..2000). maxKcal — то, что реально ушло в запрос; custom — строка поля ввода.
// Смена maxKcal пересоздаёт load → перезапрос. Напитки бэкенд исключает.
export function CaloriesScreen() {
  const nav = useNavigate();
  const [maxKcal, setMaxKcal] = useState<number>(DEFAULT_KCAL);
  const [custom, setCustom] = useState<string>(String(DEFAULT_KCAL));
  const [items, setItems] = useState<RecipeByCalories[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(() => {
    setError(null);
    setItems(null);
    void endpoints.getRecipesByCalories(maxKcal).then(
      (r) => setItems(r.items),
      (e) => setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить'),
    );
  }, [maxKcal]);

  useEffect(() => {
    // Разовая загрузка при смене порога — намеренный fetch-in-effect (план без TanStack).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  // Пресет: мгновенно меняем порог и синхронизируем поле ввода (debounce не нужен — явный выбор).
  function pickPreset(value: number) {
    if (timer.current) clearTimeout(timer.current);
    setCustom(String(value));
    setMaxKcal(value);
  }

  // Своё число: обновляем поле сразу, а запрос — с debounce и клампом. Пустое/нечисловое не шлём.
  function onCustomChange(raw: string) {
    setCustom(raw);
    if (timer.current) clearTimeout(timer.current);
    const n = Number(raw);
    if (!raw.trim() || !Number.isFinite(n)) return;
    timer.current = setTimeout(() => setMaxKcal(clamp(Math.round(n))), 400);
  }

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <div className="px-4 pt-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} aria-label="Назад" className="text-xl text-ink/60">
          ←
        </button>
        <h1 className="text-2xl font-bold text-ink">Калории</h1>
      </div>

      <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink/50">Не более…</p>
      <div className="mb-3 flex flex-wrap gap-2">
        {PRESETS.map((value) => (
          <button
            key={value}
            onClick={() => pickPreset(value)}
            aria-pressed={maxKcal === value}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition active:scale-[0.98] ${
              maxKcal === value
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-white text-ink/70 ring-1 ring-inset ring-black/10'
            }`}
          >
            {value}
          </button>
        ))}
        <label className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm text-ink/70 ring-1 ring-inset ring-black/10">
          <input
            type="number"
            inputMode="numeric"
            min={MIN_KCAL}
            max={MAX_KCAL}
            value={custom}
            onChange={(e) => onCustomChange(e.target.value)}
            aria-label="Своё значение калорий"
            placeholder="своё"
            className="w-16 bg-transparent text-center outline-none"
          />
          <span className="text-ink/50">ккал</span>
        </label>
      </div>

      <p className="mb-4 text-sm text-ink/60">Рецепты до {maxKcal} ккал на порцию, от самых лёгких</p>

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : items === null ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Нет рецептов в этом пределе" hint="Поднимите порог калорий." />
      ) : (
        <div className="stagger space-y-4">
          {items.map((r) => (
            <RecipeCard
              key={r.id}
              title={r.title ?? ''}
              imageUrl={r.imageUrl}
              cookTimeMin={r.cookTimeMin}
              servings={r.servings}
              kcal={r.kcal}
              onClick={() => nav(`/recipe/${r.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
